/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDarkMode } from "../../context/DarkModeContext";

interface JobForm {
  title: string;
  company: string;
  location: string;
  salary: string;
  type: string;
  description: string;
  category: string;
  deadline: string;
  imageUrl?: string;
  image?: File;
  imagePreview?: string;
}

export default function CreateJob() {
  const navigate = useNavigate();
  const { isDark } = useDarkMode();
  const [form, setForm] = useState<JobForm>({
    title: "",
    company: "",
    location: "",
    salary: "",
    type: "full_time",
    description: "",
    category: "",
    deadline: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkText, setLinkText] = useState("");

  // Map human-readable labels to enum values
  const jobTypeOptions = [
    { value: "full_time", label: "Full-time" },
    { value: "part_time", label: "Part-time" },
    { value: "contract", label: "Contract" },
    { value: "freelance", label: "Freelance" },
    { value: "internship", label: "Internship" },
    { value: "remote", label: "Remote" },
    { value: "temporary", label: "Temporary" },
  ];

  // Map category human-readable labels to enum values
  const categoryOptions = [
    { value: "technology", label: "Technology" },
    { value: "healthcare", label: "Healthcare" },
    { value: "finance", label: "Finance" },
    { value: "education", label: "Education" },
    { value: "marketing", label: "Marketing" },
    { value: "sales", label: "Sales" },
    { value: "design", label: "Design" },
    { value: "engineering", label: "Engineering" },
    { value: "customer_service", label: "Customer Support" },
    { value: "human_resources", label: "Human Resources" },
    { value: "operations", label: "Operations" },
    { value: "legal", label: "Legal" },
    { value: "hospitality", label: "Hospitality" },
    { value: "construction", label: "Construction" },
    { value: "transportation", label: "Transportation" },
    { value: "retail", label: "Retail" },
    { value: "manufacturing", label: "Manufacturing" },
    { value: "agriculture", label: "Agriculture" },
    { value: "entertainment", label: "Entertainment" },
    { value: "other", label: "Other" },
  ];

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        setError("Please select a valid image file");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError("Image size should be less than 5MB");
        return;
      }

      setError("");

      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setForm({
        ...form,
        image: file,
        imagePreview: previewUrl,
      });
    }
  };

  const removeImage = () => {
    if (form.imagePreview) {
      URL.revokeObjectURL(form.imagePreview);
    }
    setForm({
      ...form,
      image: undefined,
      imagePreview: undefined,
    });
  };

  // NEW: Function to insert link
  const insertLink = () => {
    if (!linkUrl.trim()) {
      setError("Please enter a URL");
      return;
    }

    const text = linkText.trim() || linkUrl;
    const markdownLink = `[${text}](${linkUrl})`;

    // Insert at cursor position or append to end
    const textarea = document.querySelector(
      'textarea[name="description"]',
    ) as HTMLTextAreaElement;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const currentValue = form.description;

      const newValue =
        currentValue.substring(0, start) +
        markdownLink +
        currentValue.substring(end);
      setForm({ ...form, description: newValue });

      // Reset link form
      setLinkUrl("");
      setLinkText("");
      setShowLinkModal(false);
    }
  };

  // NEW: Function to open link modal
  const openLinkModal = () => {
    setShowLinkModal(true);
    setLinkUrl("");
    setLinkText("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("token");

      // Validate deadline
      if (form.deadline) {
        const deadlineDate = new Date(form.deadline);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (deadlineDate < today) {
          throw new Error("Deadline cannot be in the past");
        }
      }

      // Create JSON payload
      const payload = {
        title: form.title,
        company: form.company,
        location: form.location,
        salary: form.salary,
        type: form.type,
        description: form.description,
        category: form.category,
        deadline: form.deadline || null,
      };

      console.log("Sending payload:", payload);

      const response = await fetch(`${import.meta.env.VITE_API_BASE}/jobs`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Server error response:", errorData);
        throw new Error(
          errorData.error || errorData.message || "Failed to create job",
        );
      }

      const data = await response.json();
      console.log("Job created successfully:", data);

      // Handle image upload separately if needed
      if (form.image && data.job && data.job.id && token) {
        await uploadJobImage(data.job.id, form.image, token);
      }

      // NEW: Trigger job alerts after successful job creation
      if (data.job && data.job.id) {
        try {
          await triggerJobAlerts(data.job.id, token!);
        } catch (alertError) {
          console.warn("Job alerts failed to send:", alertError);
          // Don't fail the job creation if alerts fail
        }
      }

      // Clean up preview URL
      if (form.imagePreview) {
        URL.revokeObjectURL(form.imagePreview);
      }

      navigate("/dashboard/admin-panel", {
        state: { activeTab: "jobs" },
      });
    } catch (err: any) {
      console.error("Error creating job:", err);
      setError(err.message || "An error occurred while creating the job");
    } finally {
      setLoading(false);
    }
  };

  // NEW: Function to trigger job alerts
  const triggerJobAlerts = async (jobId: string, token: string) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE}/jobs/${jobId}/alerts`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        console.warn("Failed to trigger job alerts");
      } else {
        const alertData = await response.json();
        console.log("Job alerts triggered:", alertData);
      }
    } catch (error) {
      console.error("Error triggering job alerts:", error);
      throw error;
    }
  };

  // Separate function for image upload (optional)
  const uploadJobImage = async (jobId: string, image: File, token: string) => {
    try {
      const imageFormData = new FormData();
      imageFormData.append("image", image);

      const API_BASE_URL = import.meta.env.VITE_API_BASE;

      const response = await fetch(`${API_BASE_URL}/jobs/${jobId}/image`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: imageFormData,
      });

      if (!response.ok) {
        console.warn(
          "Failed to upload image, but job was created successfully",
        );
      } else {
        console.log("Image uploaded successfully");
      }
    } catch (error) {
      console.warn("Error uploading image:", error);
    }
  };

  // Calculate minimum date for deadline (today)
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  return (
    <div
      className={`min-h-screen p-6 ${
        isDark
          ? "bg-gradient-to-br from-gray-900 to-gray-800"
          : "bg-gradient-to-br from-green-50 to-sky-50"
      }`}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1
            className={`text-3xl font-bold bg-gradient-to-r from-green-600 to-sky-500 bg-clip-text text-transparent mb-2`}
          >
            Create New Job
          </h1>
          <p className={isDark ? "text-gray-300" : "text-gray-600"}>
            Add a new job posting to the platform
          </p>
        </div>

        {/* Form Card - Landscape Layout */}
        <div
          className={`shadow-lg rounded-2xl overflow-hidden border ${
            isDark ? "bg-gray-800 border-gray-700" : "bg-white border-green-100"
          }`}
        >
          {/* Card Header */}
          <div className="px-8 py-6 bg-gradient-to-r from-green-500 to-sky-500">
            <h2 className="text-xl font-semibold text-white">Job Details</h2>
          </div>

          {/* Form Content - Landscape Layout */}
          <div className="p-8">
            {error && (
              <div
                className={`mb-6 p-4 rounded-lg border ${
                  isDark
                    ? "bg-red-900/20 border-red-700 text-red-300"
                    : "bg-red-100 border-red-200 text-red-700"
                }`}
              >
                <strong>Error:</strong> {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* First Row: Image Upload */}
              <div>
                <label
                  className={`block text-sm font-medium mb-3 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Job Poster/Image (Optional)
                </label>
                <p
                  className={`text-sm mb-4 ${
                    isDark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Note: Image upload will be handled after job creation
                </p>

                {form.imagePreview ? (
                  <div className="flex items-center space-x-6">
                    <div className="relative">
                      <img
                        src={form.imagePreview}
                        alt="Job poster preview"
                        className={`w-48 h-32 object-cover rounded-lg border-2 ${
                          isDark ? "border-green-600" : "border-green-200"
                        }`}
                      />
                      <button
                        type="button"
                        onClick={removeImage}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                    <p
                      className={`text-sm ${
                        isDark ? "text-green-400" : "text-green-600"
                      }`}
                    >
                      Image selected (will upload after job creation) ✓
                    </p>
                  </div>
                ) : (
                  <div
                    className={`border-2 border-dashed rounded-lg p-6 hover:border-green-300 transition-colors max-w-md ${
                      isDark
                        ? "border-green-600 hover:border-green-500 bg-gray-700/50"
                        : "border-green-200 hover:border-green-300"
                    }`}
                  >
                    <svg
                      className={`mx-auto h-12 w-12 ${
                        isDark ? "text-gray-500" : "text-gray-400"
                      }`}
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="mt-4 flex text-sm">
                      <label
                        className={`relative cursor-pointer rounded-md font-medium focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-green-500 mx-auto ${
                          isDark
                            ? "text-green-400 hover:text-green-300 bg-gray-800"
                            : "text-green-600 hover:text-green-500 bg-white"
                        }`}
                      >
                        <span>Upload an image</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="sr-only"
                        />
                      </label>
                    </div>
                    <p
                      className={`text-xs mt-2 text-center ${
                        isDark ? "text-gray-500" : "text-gray-500"
                      }`}
                    >
                      PNG, JPG, GIF up to 5MB
                    </p>
                  </div>
                )}
              </div>

              {/* Second Row: Basic Information - 3 columns */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Job Title */}
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Job Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    placeholder="e.g., Senior Frontend Developer"
                    value={form.title}
                    onChange={handleChange}
                    required
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent transition-all duration-200 ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "border-green-200 text-gray-900"
                    }`}
                  />
                </div>

                {/* Company */}
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Company Name *
                  </label>
                  <input
                    type="text"
                    name="company"
                    placeholder="e.g., Tech Solutions Inc."
                    value={form.company}
                    onChange={handleChange}
                    required
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent transition-all duration-200 ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "border-green-200 text-gray-900"
                    }`}
                  />
                </div>

                {/* Location */}
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Location *
                  </label>
                  <input
                    type="text"
                    name="location"
                    placeholder="e.g., Nairobi, Kenya or Remote"
                    value={form.location}
                    onChange={handleChange}
                    required
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent transition-all duration-200 ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "border-green-200 text-gray-900"
                    }`}
                  />
                </div>
              </div>

              {/* Third Row: Job Details - 3 columns */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Salary */}
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Salary *
                  </label>
                  <input
                    type="text"
                    name="salary"
                    placeholder="e.g., KES 80,000 / month"
                    value={form.salary}
                    onChange={handleChange}
                    required
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent transition-all duration-200 ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "border-green-200 text-gray-900"
                    }`}
                  />
                </div>

                {/* Job Type */}
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Job Type *
                  </label>
                  <select
                    name="type"
                    value={form.type}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent transition-all duration-200 ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "border-green-200 text-gray-900"
                    }`}
                  >
                    {jobTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Category */}
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Category *
                  </label>
                  <select
                    name="category"
                    value={form.category}
                    onChange={handleChange}
                    required
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent transition-all duration-200 ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "border-green-200 text-gray-900"
                    }`}
                  >
                    <option value="">Select a category</option>
                    {categoryOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Fourth Row: Deadline */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Application Deadline */}
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Application Deadline (Optional)
                  </label>
                  <input
                    type="date"
                    name="deadline"
                    value={form.deadline}
                    onChange={handleChange}
                    min={getTodayDate()}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent transition-all duration-200 ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "border-green-200 text-gray-900"
                    }`}
                  />
                  <p
                    className={`text-sm mt-1 ${
                      isDark ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Leave empty if there's no specific deadline
                  </p>
                </div>
              </div>

              {/* Fifth Row: Description - Full width */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label
                    className={`block text-sm font-medium ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Job Description *
                  </label>
                  {/* NEW: Insert Link Button */}
                  <button
                    type="button"
                    onClick={openLinkModal}
                    className={`text-sm px-3 py-1 rounded border transition-colors ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-green-400 hover:bg-gray-600"
                        : "bg-green-50 border-green-200 text-green-600 hover:bg-green-100"
                    }`}
                  >
                    Insert Link
                  </button>
                </div>
                <textarea
                  name="description"
                  placeholder="Enter detailed job description, requirements, responsibilities..."
                  value={form.description}
                  onChange={handleChange}
                  required
                  rows={6}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent transition-all duration-200 resize-vertical ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                      : "border-green-200 text-gray-900"
                  }`}
                />
                <p
                  className={`text-sm mt-1 ${
                    isDark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Use markdown syntax for formatting. Links will be formatted as
                  [text](url)
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-4 pt-6">
                <button
                  type="button"
                  onClick={() => {
                    // Clean up preview URL when canceling
                    if (form.imagePreview) {
                      URL.revokeObjectURL(form.imagePreview);
                    }
                    navigate("/dashboard/admin-panel", {
                      state: { activeTab: "jobs" },
                    });
                  }}
                  className={`flex-1 py-4 px-6 border font-medium rounded-lg transition-all duration-300 shadow-sm text-lg ${
                    isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 py-4 px-6 bg-gradient-to-r from-green-500 to-sky-500 text-white font-medium rounded-lg hover:from-green-600 hover:to-sky-600 disabled:opacity-70 transition-all duration-300 shadow-md hover:shadow-lg text-lg"
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Creating Job...
                    </span>
                  ) : (
                    "Create Job"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* NEW: Link Insertion Modal */}
        {showLinkModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div
              className={`rounded-lg shadow-xl max-w-md w-full p-6 ${
                isDark ? "bg-gray-800" : "bg-white"
              }`}
            >
              <h3
                className={`text-lg font-semibold mb-4 ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Insert Link
              </h3>

              <div className="space-y-4">
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    URL *
                  </label>
                  <input
                    type="url"
                    value={linkUrl}
                    onChange={(e) => setLinkUrl(e.target.value)}
                    placeholder="https://example.com"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Link Text (Optional)
                  </label>
                  <input
                    type="text"
                    value={linkText}
                    onChange={(e) => setLinkText(e.target.value)}
                    placeholder="Click here"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "border-gray-300 text-gray-900"
                    }`}
                  />
                  <p
                    className={`text-xs mt-1 ${
                      isDark ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    If empty, the URL will be used as the link text
                  </p>
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowLinkModal(false)}
                  className={`flex-1 py-2 px-4 border rounded-lg transition-colors ${
                    isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={insertLink}
                  className="flex-1 py-2 px-4 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                >
                  Insert Link
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Quick Tips - Side by side with form */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div
            className={`rounded-lg border p-6 ${
              isDark
                ? "bg-gray-800 border-gray-700"
                : "bg-white border-green-200"
            }`}
          >
            <h3
              className={`text-lg font-semibold mb-3 ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              Quick Tips
            </h3>
            <ul
              className={`text-sm space-y-2 ${
                isDark ? "text-gray-300" : "text-gray-600"
              }`}
            >
              <li>• Provide clear and detailed job descriptions</li>
              <li>• Include accurate salary information</li>
              <li>• Specify if the position is remote-friendly</li>
              <li>• Set a realistic application deadline</li>
              <li>• Use relevant keywords for better searchability</li>
              <li>• Add an attractive image to make your job stand out</li>
              <li>• Use the "Insert Link" button to add clickable URLs</li>
            </ul>
          </div>

          <div
            className={`rounded-lg border p-6 ${
              isDark
                ? "bg-gray-800 border-gray-700"
                : "bg-white border-green-200"
            }`}
          >
            <h3
              className={`text-lg font-semibold mb-3 ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              Best Practices
            </h3>
            <ul
              className={`text-sm space-y-2 ${
                isDark ? "text-gray-300" : "text-gray-600"
              }`}
            >
              <li>• Keep job titles clear and specific</li>
              <li>• Highlight key requirements and qualifications</li>
              <li>• Mention company culture and benefits</li>
              <li>• Be transparent about the hiring process</li>
              <li>• Include contact information for inquiries</li>
              <li>• Review and proofread before posting</li>
              <li>• Use descriptive text for links instead of raw URLs</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
